import React, { useState } from 'react';
import './VehicleApplicationForm.css';
import { supabase } from '../supabase';  

const VehicleApplicationForm = () => {
  const [applicationType, setApplicationType] = useState('');
  const [formData, setFormData] = useState({
    // Applicant Details
    fullName: '',
    businessName: '',
    residentialAddress: '',
    phone: '',
    email: '',
    occupation: '',
    identificationType: '',
    idNumber: '',
    driversLicence: '',
    licenceExpiry: '',
    // Vehicle Request
    preferredVehicle: '',
    makeModel: '',
    year: '',
    intendedUse: '',
    primaryUseArea: '',
    commencementDate: '',
    // Lease Section
    leasePeriod: '',
    leasePayment: '',
    leasePaymentFrequency: '',
    securityDeposit: '',
    maintenanceResponsibility: '',
    returnDate: '',
    // Hire Purchase Section
    purchasePrice: '',
    initialDeposit: '',
    balance: '',
    repaymentPeriod: '',
    repaymentAmount: '',
    repaymentFrequency: '',
    repaymentSource: '',
    monthlyIncome: '',
    // Guarantor
    guarantorName: '',
    guarantorPhone: '',
    guarantorAddress: '',
    guarantorOccupation: '',
    guarantorRelationship: '',
    guarantorIdType: '',
    guarantorIdNumber: '',
    // Declaration
    applicantName: '',
    applicantSignature: '',
    applicantDate: '',
    guarantorNameSigned: '',
    guarantorSignature: '',
    guarantorDate: '',
    // Terms
    agreeTerms: false
  });

  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleApplicationTypeChange = (type) => {
    setApplicationType(type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus(null);

    // Validate required fields based on application type
    const requiredFields = ['fullName', 'phone', 'email', 'occupation', 'identificationType', 'idNumber', 'preferredVehicle', 'intendedUse'];
    let missingFields = false;
    requiredFields.forEach(field => {
      if (!formData[field]) missingFields = true;
    });

    if (applicationType === 'lease') {
      if (!formData.leasePeriod || !formData.leasePayment) missingFields = true;
    } else if (applicationType === 'purchase') {
      if (!formData.purchasePrice || !formData.initialDeposit || !formData.repaymentPeriod) missingFields = true;
    }

    if (!formData.guarantorName || !formData.guarantorPhone) missingFields = true;
    if (!formData.applicantName || !formData.applicantDate) missingFields = true;

    if (missingFields) {
      setFormStatus('error');
      setFormStatus('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      // Submit to Supabase
      const { error } = await supabase
        .from('vehicle_applications')
        .insert([{
          application_type: applicationType,
          full_name: formData.fullName,
          business_name: formData.businessName,
          residential_address: formData.residentialAddress,
          phone: formData.phone,
          email: formData.email,
          occupation: formData.occupation,
          identification_type: formData.identificationType,
          id_number: formData.idNumber,
          drivers_licence: formData.driversLicence,
          licence_expiry: formData.licenceExpiry,
          preferred_vehicle: formData.preferredVehicle,
          make_model: formData.makeModel,
          year: formData.year,
          intended_use: formData.intendedUse,
          primary_use_area: formData.primaryUseArea,
          commencement_date: formData.commencementDate,
          lease_period: formData.leasePeriod,
          lease_payment: formData.leasePayment,
          lease_payment_frequency: formData.leasePaymentFrequency,
          security_deposit: formData.securityDeposit,
          maintenance_responsibility: formData.maintenanceResponsibility,
          return_date: formData.returnDate,
          purchase_price: formData.purchasePrice,
          initial_deposit: formData.initialDeposit,
          balance: formData.balance,
          repayment_period: formData.repaymentPeriod,
          repayment_amount: formData.repaymentAmount,
          repayment_frequency: formData.repaymentFrequency,
          repayment_source: formData.repaymentSource,
          monthly_income: formData.monthlyIncome,
          guarantor_name: formData.guarantorName,
          guarantor_phone: formData.guarantorPhone,
          guarantor_address: formData.guarantorAddress,
          guarantor_occupation: formData.guarantorOccupation,
          guarantor_relationship: formData.guarantorRelationship,
          guarantor_id_type: formData.guarantorIdType,
          guarantor_id_number: formData.guarantorIdNumber,
          applicant_name: formData.applicantName,
          applicant_signature: formData.applicantSignature,
          applicant_date: formData.applicantDate,
          guarantor_name_signed: formData.guarantorNameSigned,
          guarantor_signature: formData.guarantorSignature,
          guarantor_date: formData.guarantorDate,
          status: 'pending'
        }]);

      if (error) throw error;
      
      setFormStatus('success');
      setFormData({
        fullName: '',
        businessName: '',
        residentialAddress: '',
        phone: '',
        email: '',
        occupation: '',
        identificationType: '',
        idNumber: '',
        driversLicence: '',
        licenceExpiry: '',
        preferredVehicle: '',
        makeModel: '',
        year: '',
        intendedUse: '',
        primaryUseArea: '',
        commencementDate: '',
        leasePeriod: '',
        leasePayment: '',
        leasePaymentFrequency: '',
        securityDeposit: '',
        maintenanceResponsibility: '',
        returnDate: '',
        purchasePrice: '',
        initialDeposit: '',
        balance: '',
        repaymentPeriod: '',
        repaymentAmount: '',
        repaymentFrequency: '',
        repaymentSource: '',
        monthlyIncome: '',
        guarantorName: '',
        guarantorPhone: '',
        guarantorAddress: '',
        guarantorOccupation: '',
        guarantorRelationship: '',
        guarantorIdType: '',
        guarantorIdNumber: '',
        applicantName: '',
        applicantSignature: '',
        applicantDate: '',
        guarantorNameSigned: '',
        guarantorSignature: '',
        guarantorDate: '',
        agreeTerms: false
      });
      setApplicationType('');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="application-form-page">
      <section className="form-hero">
        <h1>ABVIG Vehicle Application Form</h1>
        <p>Single Vehicle Finance & Use Application</p>
      </section>

      <section className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Application Type — Select ONE</h2>
              <div className="application-type-selector">
                <button 
                  className={`type-btn ${applicationType === 'lease' ? 'active' : ''}`}
                  onClick={() => handleApplicationTypeChange('lease')}
                >
                  ☐ VEHICLE LEASE
                </button>
                <button 
                  className={`type-btn ${applicationType === 'purchase' ? 'active' : ''}`}
                  onClick={() => handleApplicationTypeChange('purchase')}
                >
                  ☐ HIRE PURCHASE
                </button>
              </div>
            </div>

            {formStatus === 'success' && (
              <div className="form-success">
                <h3>✓ Application Submitted!</h3>
                <p>Your vehicle application has been received. Our team will contact you within 48 hours.</p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="form-error">
                <h3>⚠️ Submission Error</h3>
                <p>There was a problem submitting your application. Please fill in all required fields.</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Application Date & No */}
              <div className="form-row two-col">
                <div className="form-group">
                  <label>Application Date *</label>
                  <input type="date" name="applicationDate" value={new Date().toISOString().split('T')[0]} disabled />
                </div>
                <div className="form-group">
                  <label>Application No.</label>
                  <input type="text" value={`ABVIG-${Date.now().toString().slice(-6)}`} disabled />
                </div>
              </div>

              {/* 1. APPLICANT DETAILS */}
              <div className="form-section-title">
                <h3>1. APPLICANT DETAILS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Full Name / Registered Business Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Enter full name or business name"
                  />
                </div>
                <div className="form-group">
                  <label>Residential / Business Address *</label>
                  <input
                    type="text"
                    name="residentialAddress"
                    value={formData.residentialAddress}
                    onChange={handleChange}
                    required
                    placeholder="Enter address"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+234 800 000 0000"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Occupation / Nature of Business *</label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    required
                    placeholder="Enter occupation"
                  />
                </div>
                <div className="form-group">
                  <label>Means of Identification *</label>
                  <select
                    name="identificationType"
                    value={formData.identificationType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select ID Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Driver's Licence">Driver's Licence</option>
                    <option value="International Passport">International Passport</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>ID Number *</label>
                  <input
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleChange}
                    required
                    placeholder="Enter ID number"
                  />
                </div>
                <div className="form-group">
                  <label>Driver's Licence Number</label>
                  <input
                    type="text"
                    name="driversLicence"
                    value={formData.driversLicence}
                    onChange={handleChange}
                    placeholder="Enter driver's licence number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Driver's Licence Expiry Date</label>
                <input
                  type="date"
                  name="licenceExpiry"
                  value={formData.licenceExpiry}
                  onChange={handleChange}
                />
              </div>

              {/* 2. VEHICLE REQUEST */}
              <div className="form-section-title">
                <h3>2. VEHICLE REQUEST</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Preferred Vehicle *</label>
                  <input
                    type="text"
                    name="preferredVehicle"
                    value={formData.preferredVehicle}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Toyota Hilux"
                  />
                </div>
                <div className="form-group">
                  <label>Make / Model *</label>
                  <input
                    type="text"
                    name="makeModel"
                    value={formData.makeModel}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Toyota / Hilux 2024"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    placeholder="e.g., 2024"
                  />
                </div>
                <div className="form-group">
                  <label>Intended Use *</label>
                  <select
                    name="intendedUse"
                    value={formData.intendedUse}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select intended use</option>
                    <option value="Personal">Personal</option>
                    <option value="Business">Business</option>
                    <option value="Commercial Transportation">Commercial Transportation</option>
                    <option value="Corporate / Official Use">Corporate / Official Use</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Primary Area of Use</label>
                  <input
                    type="text"
                    name="primaryUseArea"
                    value={formData.primaryUseArea}
                    onChange={handleChange}
                    placeholder="e.g., Lagos, Abuja"
                  />
                </div>
                <div className="form-group">
                  <label>Expected Commencement Date</label>
                  <input
                    type="date"
                    name="commencementDate"
                    value={formData.commencementDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* 3. VEHICLE LEASE SECTION */}
              {applicationType === 'lease' && (
                <div className="conditional-section">
                  <div className="form-section-title lease-title">
                    <h3>3. VEHICLE LEASE</h3>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Preferred Lease Period *</label>
                      <select
                        name="leasePeriod"
                        value={formData.leasePeriod}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select period</option>
                        <option value="1 Month">1 Month</option>
                        <option value="3 Months">3 Months</option>
                        <option value="6 Months">6 Months</option>
                        <option value="12 Months">12 Months</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Agreed/Proposed Lease Payment (₦) *</label>
                      <input
                        type="number"
                        name="leasePayment"
                        value={formData.leasePayment}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Payment Frequency</label>
                      <select
                        name="leasePaymentFrequency"
                        value={formData.leasePaymentFrequency}
                        onChange={handleChange}
                      >
                        <option value="">Select frequency</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Security Deposit (₦)</label>
                      <input
                        type="number"
                        name="securityDeposit"
                        value={formData.securityDeposit}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Who will be responsible for fuel and routine vehicle maintenance?</label>
                    <select
                      name="maintenanceResponsibility"
                      value={formData.maintenanceResponsibility}
                      onChange={handleChange}
                    >
                      <option value="">Select responsibility</option>
                      <option value="Applicant">Applicant</option>
                      <option value="ABVIG">ABVIG</option>
                      <option value="As stated in the Lease Agreement">As stated in the Lease Agreement</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Expected Vehicle Return Date</label>
                    <input
                      type="date"
                      name="returnDate"
                      value={formData.returnDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="legal-notice">
                    <p>⚠️ I understand that the vehicle remains the property of ABVIG throughout the lease period and must be returned in accordance with the applicable Lease Agreement.</p>
                  </div>
                </div>
              )}

              {/* 4. HIRE PURCHASE SECTION */}
              {applicationType === 'purchase' && (
                <div className="conditional-section">
                  <div className="form-section-title purchase-title">
                    <h3>4. HIRE PURCHASE</h3>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Vehicle Purchase Price (₦) *</label>
                      <input
                        type="number"
                        name="purchasePrice"
                        value={formData.purchasePrice}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <label>Proposed Initial Deposit (₦) *</label>
                      <input
                        type="number"
                        name="initialDeposit"
                        value={formData.initialDeposit}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Proposed Balance (₦)</label>
                      <input
                        type="number"
                        name="balance"
                        value={formData.balance}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <label>Proposed Repayment Period *</label>
                      <input
                        type="text"
                        name="repaymentPeriod"
                        value={formData.repaymentPeriod}
                        onChange={handleChange}
                        required
                        placeholder="e.g., 12 months"
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Proposed Repayment Amount (₦) *</label>
                      <input
                        type="number"
                        name="repaymentAmount"
                        value={formData.repaymentAmount}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <label>Payment Frequency</label>
                      <select
                        name="repaymentFrequency"
                        value={formData.repaymentFrequency}
                        onChange={handleChange}
                      >
                        <option value="">Select frequency</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Source of Repayment</label>
                      <select
                        name="repaymentSource"
                        value={formData.repaymentSource}
                        onChange={handleChange}
                      >
                        <option value="">Select source</option>
                        <option value="Salary">Salary</option>
                        <option value="Business Income">Business Income</option>
                        <option value="Contract/Professional Income">Contract/Professional Income</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Estimated Monthly Income (₦)</label>
                      <input
                        type="number"
                        name="monthlyIncome"
                        value={formData.monthlyIncome}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="legal-notice">
                    <p>⚠️ I understand that completion of this application does not by itself transfer ownership of the vehicle to me. Ownership and all other rights shall be governed by the formal Hire Purchase Agreement executed with ABVIG.</p>
                  </div>
                </div>
              )}

              {/* 5. GUARANTOR / REFERENCE */}
              <div className="form-section-title">
                <h3>5. GUARANTOR / REFERENCE</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor Full Name *</label>
                  <input
                    type="text"
                    name="guarantorName"
                    value={formData.guarantorName}
                    onChange={handleChange}
                    required
                    placeholder="Enter guarantor name"
                  />
                </div>
                <div className="form-group">
                  <label>Guarantor Phone Number *</label>
                  <input
                    type="tel"
                    name="guarantorPhone"
                    value={formData.guarantorPhone}
                    onChange={handleChange}
                    required
                    placeholder="+234 800 000 0000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Guarantor Address</label>
                <input
                  type="text"
                  name="guarantorAddress"
                  value={formData.guarantorAddress}
                  onChange={handleChange}
                  placeholder="Enter address"
                />
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor Occupation / Business</label>
                  <input
                    type="text"
                    name="guarantorOccupation"
                    value={formData.guarantorOccupation}
                    onChange={handleChange}
                    placeholder="Enter occupation"
                  />
                </div>
                <div className="form-group">
                  <label>Relationship to Applicant</label>
                  <input
                    type="text"
                    name="guarantorRelationship"
                    value={formData.guarantorRelationship}
                    onChange={handleChange}
                    placeholder="e.g., Family, Colleague"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor Means of Identification</label>
                  <select
                    name="guarantorIdType"
                    value={formData.guarantorIdType}
                    onChange={handleChange}
                  >
                    <option value="">Select ID Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Driver's Licence">Driver's Licence</option>
                    <option value="International Passport">International Passport</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Guarantor ID Number</label>
                  <input
                    type="text"
                    name="guarantorIdNumber"
                    value={formData.guarantorIdNumber}
                    onChange={handleChange}
                    placeholder="Enter ID number"
                  />
                </div>
              </div>

              {/* 6. APPLICANT UNDERTAKING & DECLARATION */}
              <div className="form-section-title">
                <h3>6. APPLICANT UNDERTAKING & DECLARATION</h3>
              </div>

              <div className="declaration-text">
                <p>I declare that all information provided in this application is true, accurate and complete. I understand that ABVIG may verify the information and documents provided and may reject this application where information supplied is false, incomplete or cannot be verified.</p>
                <p>I agree that any vehicle offered to me by ABVIG shall be used responsibly and only for lawful purposes. I shall not sell, sublease, transfer, pledge, assign, alter, conceal, abandon or otherwise dispose of the vehicle or any interest in it without ABVIG's prior written consent.</p>
                <p>I understand that I will be responsible for all payments and other obligations applicable to the arrangement approved by ABVIG.</p>
                <p>I agree to comply with the applicable Vehicle Lease Agreement or Hire Purchase Agreement, including provisions relating to payment default, damage, loss, insurance, maintenance, recovery of the vehicle and other applicable charges.</p>
                <p>I authorize ABVIG to verify the information and documents supplied by me for the purpose of assessing this application.</p>
                <p>I understand that this application is not an approval, sale, lease or transfer of ownership. The arrangement shall only become effective upon ABVIG's approval and execution of the appropriate formal agreement.</p>
                <p>I confirm that I have read and understood this declaration.</p>
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>Applicant's Full Name *</label>
                  <input
                    type="text"
                    name="applicantName"
                    value={formData.applicantName}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Signature</label>
                  <input
                    type="text"
                    name="applicantSignature"
                    value={formData.applicantSignature}
                    onChange={handleChange}
                    placeholder="Digital signature"
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="applicantDate"
                    value={formData.applicantDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* 7. GUARANTOR DECLARATION */}
              <div className="form-section-title">
                <h3>7. GUARANTOR DECLARATION</h3>
              </div>

              <div className="declaration-text">
                <p>I confirm that I know the applicant and have provided my details as a guarantor/reference for this application.</p>
                <p>I authorize ABVIG to verify the information I have provided.</p>
                <p>Where I sign a separate guarantee or other undertaking with ABVIG, I agree to be bound by the terms of that document.</p>
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>Guarantor's Full Name</label>
                  <input
                    type="text"
                    name="guarantorNameSigned"
                    value={formData.guarantorNameSigned}
                    onChange={handleChange}
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Signature</label>
                  <input
                    type="text"
                    name="guarantorSignature"
                    value={formData.guarantorSignature}
                    onChange={handleChange}
                    placeholder="Digital signature"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    name="guarantorDate"
                    value={formData.guarantorDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Terms */}
              <div className="form-group checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    required
                  />
                  I confirm that I have read and understood all terms and conditions.
                </label>
              </div>

              <button type="submit" className="btn-gold submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VehicleApplicationForm;